import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, CheckIcon, FolderIcon } from '@heroicons/react/24/outline';
import { Issue, IssueStatus } from '../../types';
import { useToast } from '../ToastContainer';
import api from '../../services/api';

interface Project {
  id: string;
  name: string;
  key: string;
  description?: string;
  icon?: string;
  userRole: string;
}

interface GlobalIssueSelectorProps {
  selectedIssueIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onSelect: (issueIds: string[]) => void;
}

const GlobalIssueSelector: React.FC<GlobalIssueSelectorProps> = ({
  selectedIssueIds,
  isOpen,
  onClose,
  onSelect
}) => {
  const [accessibleProjects, setAccessibleProjects] = useState<Project[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
  const [issues, setIssues] = useState<Issue[]>([]);
  const [filteredIssues, setFilteredIssues] = useState<Issue[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(selectedIssueIds));
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingIssues, setIsLoadingIssues] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [groupByProject, setGroupByProject] = useState<boolean>(true);
  const { showError } = useToast();

  // 專案顏色映射
  const projectColors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', 
    '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#6366f1'
  ];

  const getProjectColor = (projectId: string) => {
    const index = accessibleProjects.findIndex(p => p.id === projectId);
    return projectColors[index % projectColors.length];
  };

  // 移除 HTML 標籤的函數
  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    } catch (error) {
      // 如果 DOMParser 失敗，使用正則表達式作為備選方案
      return html.replace(/<[^>]*>/g, '').replace(/&[^;]+;/g, ' ').trim();
    }
  };

  // 載入可存取的專案列表
  useEffect(() => {
    if (isOpen) {
      loadAccessibleProjects();
      // 重置狀態
      setSelectedProjectIds(new Set());
      setIssues([]);
      setFilteredIssues([]);
      setSearchTerm('');
      setStatusFilter('ALL');
    }
  }, [isOpen]);

  // 當有已選議題時，自動載入包含這些議題的專案
  useEffect(() => {
    console.log('Effect check:', { isOpen, selectedIssueIdsLength: selectedIssueIds.length, accessibleProjectsLength: accessibleProjects.length });
    if (isOpen && selectedIssueIds.length > 0 && accessibleProjects.length > 0) {
      console.log('Loading projects for selected issues...');
      loadProjectsForSelectedIssues();
    }
  }, [isOpen, selectedIssueIds, accessibleProjects]);

  // 更新選中狀態
  useEffect(() => {
    setSelectedIds(new Set(selectedIssueIds));
  }, [selectedIssueIds]);

  // 當選擇的專案改變時，載入議題
  useEffect(() => {
    if (selectedProjectIds.size > 0) {
      loadIssuesFromSelectedProjects();
    } else {
      setIssues([]);
    }
  }, [selectedProjectIds]);

  // 篩選議題
  useEffect(() => {
    let filtered = issues;

    // 搜尋篩選
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(issue => 
        issue.title.toLowerCase().includes(term) ||
        issue.key.toLowerCase().includes(term) ||
        issue.description?.toLowerCase().includes(term) ||
        issue.project?.name.toLowerCase().includes(term)
      );
    }

    // 狀態篩選
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(issue => {
        if (issue.boardStatus) {
          return issue.boardStatus.id === statusFilter;
        }
        return issue.status === statusFilter;
      });
    }

    // 排除已封存和已刪除的議題
    filtered = filtered.filter(issue => !issue.archived && !issue.deleted);

    setFilteredIssues(filtered);
  }, [issues, searchTerm, statusFilter]);

  const loadAccessibleProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const response = await api.get('/global-worklogs/accessible-projects');
      setAccessibleProjects(response.data);
    } catch (error) {
      console.error('Failed to load accessible projects:', error);
      showError('載入可存取專案失敗');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadIssuesFromSelectedProjects = async () => {
    try {
      setIsLoadingIssues(true);
      const projectIds = Array.from(selectedProjectIds).filter(id => 
        id && id !== 'undefined' && id !== 'null' && id.trim() !== ''
      );
      
      if (projectIds.length === 0) {
        setIssues([]);
        return;
      }
      
      // 並行載入多個專案的議題
      const issuePromises = projectIds.map(async (projectId) => {
        try {
          const response = await api.get(`/projects/id/${projectId}/issues`);
          return response.data?.data?.issues || [];
        } catch (error) {
          console.error(`Failed to load issues for project ${projectId}:`, error);
          return [];
        }
      });

      const issueArrays = await Promise.all(issuePromises);
      const allIssues = issueArrays.flat();
      
      setIssues(allIssues);
    } catch (error) {
      console.error('Failed to load issues:', error);
      showError('載入議題失敗');
    } finally {
      setIsLoadingIssues(false);
    }
  };

  // 載入包含已選議題的專案
  const loadProjectsForSelectedIssues = async () => {
    if (selectedIssueIds.length === 0) return;
    
    try {
      setIsLoadingIssues(true);
      
      // 載入已選議題的詳細資料以獲取專案資訊
      const issuePromises = selectedIssueIds.map(async (issueId) => {
        try {
          const response = await api.get(`/issues/${issueId}`);
          console.log(`Raw response for issue ${issueId}:`, response.data);
          
          // 檢查響應格式並提取實際數據
          if (response.data?.success && response.data?.data?.issue) {
            console.log(`Extracted issue data for ${issueId}:`, response.data.data.issue);
            return response.data.data.issue;
          } else if (response.data?.success && response.data?.data) {
            console.log(`Extracted data for issue ${issueId}:`, response.data.data);
            return response.data.data;
          } else if (response.data && !response.data.success) {
            // 直接返回的議題數據
            console.log(`Direct data for issue ${issueId}:`, response.data);
            return response.data;
          }
          console.log(`No valid data for issue ${issueId}`);
          return null;
        } catch (error) {
          console.error(`Failed to load issue ${issueId}:`, error);
          return null;
        }
      });

      const selectedIssues = (await Promise.all(issuePromises)).filter(Boolean);
      console.log('Selected issues loaded:', selectedIssues.length);
      
      // 取得這些議題所屬的專案ID，過濾掉無效的ID
      const validProjectIds = selectedIssues
        .map(issue => {
          console.log('Issue:', issue.key, 'ProjectId:', issue.projectId);
          return issue.projectId;
        })
        .filter(projectId => projectId && projectId !== 'undefined' && projectId !== 'null');
      
      console.log('Valid project IDs:', validProjectIds);
      
      if (validProjectIds.length > 0) {
        const projectIds = new Set(validProjectIds);
        console.log('Setting selected project IDs:', Array.from(projectIds));
        // 自動選中包含已選議題的專案
        setSelectedProjectIds(projectIds);
      } else {
        console.log('No valid project IDs found');
      }
      
    } catch (error) {
      console.error('Failed to load projects for selected issues:', error);
    } finally {
      setIsLoadingIssues(false);
    }
  };

  const toggleProject = (projectId: string) => {
    const newSelectedProjectIds = new Set(selectedProjectIds);
    if (newSelectedProjectIds.has(projectId)) {
      newSelectedProjectIds.delete(projectId);
    } else {
      newSelectedProjectIds.add(projectId);
    }
    setSelectedProjectIds(newSelectedProjectIds);
  };

  const toggleIssue = (issueId: string) => {
    const newSelectedIds = new Set(selectedIds);
    if (newSelectedIds.has(issueId)) {
      newSelectedIds.delete(issueId);
    } else {
      newSelectedIds.add(issueId);
    }
    setSelectedIds(newSelectedIds);
  };

  const handleConfirm = () => {
    onSelect(Array.from(selectedIds));
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  const getStatusColor = (issue: Issue) => {
    if (issue.boardStatus) {
      return issue.boardStatus.color;
    }
    // 預設狀態顏色
    switch (issue.status) {
      case IssueStatus.TODO:
        return '#6b7280';
      case IssueStatus.IN_PROGRESS:
        return '#3b82f6';
      case IssueStatus.IN_REVIEW:
        return '#f59e0b';
      case IssueStatus.DONE:
        return '#10b981';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (issue: Issue) => {
    if (issue.boardStatus) {
      return issue.boardStatus.name;
    }
    // 預設狀態文字
    switch (issue.status) {
      case IssueStatus.TODO:
        return '待辦';
      case IssueStatus.IN_PROGRESS:
        return '進行中';
      case IssueStatus.IN_REVIEW:
        return '待審核';
      case IssueStatus.DONE:
        return '完成';
      default:
        return issue.status;
    }
  };

  // 獲取唯一的狀態列表
  const uniqueStatuses = Array.from(new Set(issues.map(issue => {
    if (issue.boardStatus) {
      return { id: issue.boardStatus.id, name: issue.boardStatus.name, color: issue.boardStatus.color };
    }
    return { id: issue.status, name: getStatusText(issue), color: getStatusColor(issue) };
  }))).filter((status, index, self) => 
    index === self.findIndex(s => s.id === status.id)
  );

  // 按專案分組議題
  const groupedIssues = groupByProject 
    ? filteredIssues.reduce((groups, issue) => {
        const projectId = issue.project?.id || issue.projectId || 'unknown';
        if (!groups[projectId]) {
          groups[projectId] = [];
        }
        groups[projectId].push(issue);
        return groups;
      }, {} as Record<string, typeof filteredIssues>)
    : { all: filteredIssues };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-[60]">
      <div className="fixed inset-0 bg-black/30 z-[60]" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 z-[61]">
        <Dialog.Panel className="w-full max-w-6xl bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <Dialog.Title className="text-xl font-semibold">
                全域議題選擇器
              </Dialog.Title>
              <p className="text-sm text-gray-600 mt-1">
                已選擇 {selectedIds.size} 個議題
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* 專案選擇面板 */}
            <div className="w-1/3 border-r bg-gray-50 overflow-y-auto">
              <div className="p-4">
                <h3 className="font-medium text-gray-900 mb-3">選擇專案</h3>
                {isLoadingProjects ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {accessibleProjects.map((project) => (
                      <div
                        key={project.id}
                        onClick={() => toggleProject(project.id)}
                        className={`p-3 rounded-md cursor-pointer transition-colors ${
                          selectedProjectIds.has(project.id)
                            ? 'bg-blue-100 border-blue-300'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        } border`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                              style={{ backgroundColor: getProjectColor(project.id) }}
                            />
                            <FolderIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              <div className="font-medium text-sm">{project.name}</div>
                              <div className="text-xs text-gray-500">{project.key}</div>
                            </div>
                          </div>
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            selectedProjectIds.has(project.id) ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                          }`}>
                            {selectedProjectIds.has(project.id) && (
                              <CheckIcon className="h-3 w-3 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 議題選擇面板 */}
            <div className="flex-1 flex flex-col">
              {/* 搜尋和篩選 */}
              <div className="p-4 border-b bg-gray-50">
                <div className="flex gap-4 mb-3">
                  <div className="flex-1 relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="搜尋議題標題、代碼或描述..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ALL">所有狀態</option>
                    {uniqueStatuses.map(status => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* 顯示選項 */}
                <div className="flex items-center gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={groupByProject}
                      onChange={(e) => setGroupByProject(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">按專案分組顯示</span>
                  </label>
                </div>
              </div>

              {/* 議題列表 */}
              <div className="flex-1 overflow-y-auto p-4">
                {selectedProjectIds.size === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    請先選擇專案
                  </div>
                ) : isLoadingIssues ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : filteredIssues.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    沒有找到符合條件的議題
                  </div>
                ) : (
                  <div className="space-y-4">
                    {Object.entries(groupedIssues).map(([projectId, projectIssues]) => (
                      <div key={projectId}>
                        {groupByProject && projectId !== 'all' && (
                          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: getProjectColor(projectId) }}
                            />
                            <h4 className="font-medium text-gray-900">
                              {accessibleProjects.find(p => p.id === projectId)?.name || '未知專案'}
                            </h4>
                            <span className="text-sm text-gray-500">
                              ({projectIssues.length} 個議題)
                            </span>
                          </div>
                        )}
                        <div className="space-y-2">
                          {projectIssues.map((issue) => (
                            <div
                              key={issue.id}
                              onClick={() => toggleIssue(issue.id)}
                              className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                                selectedIds.has(issue.id)
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    {!groupByProject && (
                                      <div 
                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                        style={{ backgroundColor: getProjectColor(issue.project?.id || issue.projectId || 'unknown') }}
                                      />
                                    )}
                                    <span className="text-sm font-medium text-gray-500">{issue.key}</span>
                                    <span 
                                      className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
                                      style={{ backgroundColor: getStatusColor(issue) }}
                                    >
                                      {getStatusText(issue)}
                                    </span>
                                    {!groupByProject && (
                                      <span 
                                        className="text-xs px-2 py-0.5 rounded text-white"
                                        style={{ backgroundColor: getProjectColor(issue.project?.id || issue.projectId || 'unknown') }}
                                      >
                                        {issue.project?.name || 'Unknown Project'}
                                      </span>
                                    )}
                                  </div>
                                  <h4 className="font-medium text-gray-900">{issue.title}</h4>
                                  {issue.description && (
                                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                      {stripHtmlTags(issue.description)}
                                    </p>
                                  )}
                                  {issue.assignee && (
                                    <div className="mt-2 text-sm text-gray-500">
                                      負責人：{issue.assignee.name}
                                    </div>
                                  )}
                                </div>
                                <div className={`ml-4 flex-shrink-0 ${
                                  selectedIds.has(issue.id) ? 'text-blue-600' : 'text-gray-300'
                                }`}>
                                  <div className="w-6 h-6 rounded border-2 border-current flex items-center justify-center">
                                    {selectedIds.has(issue.id) && (
                                      <CheckIcon className="h-4 w-4" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 操作按鈕 */}
          <div className="flex justify-between items-center gap-3 p-6 border-t bg-gray-50">
            <div className="text-sm text-gray-600">
              已選擇 {selectedProjectIds.size} 個專案，{selectedIds.size} 個議題
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedIds(new Set())}
                className="text-gray-600 hover:text-gray-800"
                disabled={selectedIds.size === 0}
              >
                清除選擇
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                確認選擇 ({selectedIds.size})
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default GlobalIssueSelector;